import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Eyebrow } from "@/components/Motion";

const field = "w-full bg-transparent border-b border-matte/20 py-3 focus:outline-none focus:border-matte transition-colors";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState("");
  const [applied, setApplied] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    ship_address: "", ship_city: "", ship_zip: "",
    bill_address: "", notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const total = applied ? subtotal * (100 - applied.percent_off) / 100 : subtotal;

  const applyDiscount = async () => {
    if (!discount) return;
    try {
      const r = await api.post("/discounts/validate", { code: discount });
      setApplied({ code: discount.toUpperCase(), percent_off: r.data.percent_off });
      toast.success(`${r.data.percent_off}% discount applied`);
    } catch {
      setApplied(null);
      toast.error("Invalid discount code");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/checkout/shop", {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        discount_code: applied ? applied.code : null,
        customer: form,
      });
      localStorage.setItem("bw_last_order", res.data.order_id || "");
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-32 min-h-[60vh] max-w-[1200px] mx-auto px-6 md:px-10" data-testid="checkout-empty">
        <h1 className="font-serif text-4xl font-light mb-6">Checkout</h1>
        <p className="text-matte/50 font-serif text-2xl mb-8">There is nothing to check out yet.</p>
        <Link to="/shop" className="border border-matte px-8 py-4 text-[12px] uppercase tracking-editorial hover:bg-matte hover:text-ivory transition-colors">Browse the Shop</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-32">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pb-24">
        <Eyebrow>Secure Checkout</Eyebrow>
        <h1 className="font-serif text-4xl md:text-5xl font-light mt-3 mb-14">Checkout</h1>

        <form onSubmit={submit} className="grid md:grid-cols-12 gap-16">
          <div className="md:col-span-7 space-y-12">
            <div>
              <p className="text-[11px] uppercase tracking-editorial text-plum mb-6">Contact</p>
              <div className="grid gap-6">
                <input required placeholder="Full name" value={form.name} onChange={set("name")} className={field} data-testid="checkout-name" />
                <div className="grid md:grid-cols-2 gap-6">
                  <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="checkout-email" />
                  <input required placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} data-testid="checkout-phone" />
                </div>
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-editorial text-plum mb-6">Shipping Address</p>
              <div className="grid gap-6">
                <input required placeholder="Street address" value={form.ship_address} onChange={set("ship_address")} className={field} data-testid="checkout-ship-address" />
                <div className="grid md:grid-cols-2 gap-6">
                  <input required placeholder="City" value={form.ship_city} onChange={set("ship_city")} className={field} data-testid="checkout-ship-city" />
                  <input required placeholder="ZIP / Postal code" value={form.ship_zip} onChange={set("ship_zip")} className={field} data-testid="checkout-ship-zip" />
                </div>
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-editorial text-plum mb-6">Billing Address</p>
              <input placeholder="Billing address (if different)" value={form.bill_address} onChange={set("bill_address")} className={field} data-testid="checkout-bill-address" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-editorial text-plum mb-6">Order Notes</p>
              <textarea rows={3} placeholder="Anything we should know?" value={form.notes} onChange={set("notes")} className={`${field} resize-none`} data-testid="checkout-notes" />
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="bg-cream p-8 sticky top-28">
              <Eyebrow>Order</Eyebrow>
              <div className="mt-6 space-y-3">
                {items.map(({ product, quantity }) => (
                  <div key={product.id} className="flex justify-between text-sm text-matte/70">
                    <span>{product.title} × {quantity}</span>
                    <span>{money(product.price * quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <input placeholder="Discount code" value={discount} onChange={(e) => setDiscount(e.target.value)} className="flex-1 bg-transparent border-b border-matte/20 py-2 text-sm focus:outline-none focus:border-matte" data-testid="checkout-discount-input" />
                <button type="button" onClick={applyDiscount} className="text-[11px] uppercase tracking-wide-sm border-b border-matte hover:text-plum" data-testid="checkout-apply-discount">Apply</button>
              </div>
              {applied && (
                <div className="flex justify-between text-sm text-plum mt-4" data-testid="checkout-discount-line">
                  <span>Discount ({applied.percent_off}%)</span>
                  <span>−{money(subtotal - total)}</span>
                </div>
              )}
              <div className="flex justify-between font-serif text-2xl mt-6 pt-6 border-t border-matte/15">
                <span>Total</span><span data-testid="checkout-total">{money(total)}</span>
              </div>
              <button
                type="submit" disabled={loading}
                data-testid="pay-now-btn"
                className="w-full mt-8 bg-matte text-ivory py-4 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Redirecting…" : "Pay Securely"}
              </button>
              <p className="text-[10px] uppercase tracking-wide-sm text-matte/40 mt-4 text-center">
                All major cards accepted · Powered by Stripe
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
